import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, PLANS, LOGO, BRAND, EXTRA_CAN_PRICE } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Sparkles, Loader2, ArrowLeft, Lock, Plus, Minus } from "lucide-react";

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(params.get("plan") || "monthly_service");
  const [extraCans, setExtraCans] = useState(0);
  const [zip, setZip] = useState(params.get("zip") || "");
  const [zipState, setZipState] = useState(null); // null | {serviced, city, zip}
  const [checking, setChecking] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", street: "", city: "", state: "VA", service_date: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const verifyZip = async (e) => {
    e?.preventDefault();
    const clean = zip.replace(/\D/g, "").slice(0, 5);
    if (clean.length !== 5) { toast.error("Enter a valid 5-digit ZIP code"); return; }
    setChecking(true);
    setLeadSent(false);
    try {
      const { data } = await api.post("/service-area/check", { zip: clean });
      setZipState(data);
      if (data.serviced) toast.success(`Great — we service ${data.city || "your area"}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (params.get("zip")) verifyZip();
    // eslint-disable-next-line
  }, []);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email || !lead.phone) { toast.error("Please fill in all fields"); return; }
    try {
      await api.post("/expansion-leads", { ...lead, zip: zipState.zip });
      setLeadSent(true);
      toast.success("You're on the list!");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const checkout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Please fill in name, email and phone"); return; }
    if (!form.street.trim() || !form.city.trim() || !form.state.trim()) { toast.error("Please enter your full service address (street, city and state)"); return; }
    const fullAddress = `${form.street.trim()}, ${form.city.trim()}, ${form.state.trim().toUpperCase()} ${zipState.zip}`;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        plan, zip: zipState.zip, origin_url: window.location.origin,
        name: form.name, email: form.email, phone: form.phone,
        address: fullAddress, service_date: form.service_date, notes: form.notes,
        extra_cans: plan === "monthly_service" ? extraCans : 0,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  const selectedPlan = PLANS.find((p) => p.key === plan);
  const isMonthly = plan === "monthly_service";
  const extra = isMonthly ? extraCans : 0;
  const total = (selectedPlan?.price || 0) + extra * EXTRA_CAN_PRICE;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="bg-boco-navy text-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="booking-home-link">
            <img src={LOGO} alt="BOCO BINS" className="h-11 w-11 rounded-full object-cover" />
            <span className="font-display font-extrabold text-lg">BOCO BINS <span className="text-boco-green">LLC</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> Home</Link>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-boco-navy">Book Your Cleaning</h1>
        <p className="mt-2 text-slate-600">First, let's confirm we service your ZIP code.</p>

        {/* STEP 1: ZIP GATE */}
        <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-boco-green">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-boco-green text-boco-navy text-xs">1</span> SERVICE ZIP CODE
          </div>
          <form onSubmit={verifyZip} className="mt-4 flex gap-3">
            <Input value={zip} onChange={(e) => { setZip(e.target.value.replace(/\D/g, "").slice(0, 5)); setZipState(null); }} placeholder="Enter your ZIP code" inputMode="numeric" className="h-13 text-lg font-semibold" data-testid="booking-zip-input" />
            <button type="submit" disabled={checking} className="shrink-0 rounded-md bg-boco-navy px-6 font-bold text-white hover:bg-boco-navylight transition-colors disabled:opacity-70 flex items-center gap-2" data-testid="booking-verify-zip-btn">
              {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify"}
            </button>
          </form>

          <AnimatePresence>
            {zipState?.serviced && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-boco-greenlight border border-boco-green px-4 py-3 text-boco-greendark font-semibold" data-testid="booking-zip-ok">
                  <CheckCircle2 className="h-5 w-5" /> We service {zipState.city ? `${zipState.city}, VA` : "your area"}! Continue below.
                </div>
              </motion.div>
            )}
            {zipState && !zipState.serviced && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
                <div className="mt-4 rounded-2xl bg-amber-50 border-2 border-amber-300 p-5" data-testid="booking-zip-not-serviced">
                  <div className="flex items-center gap-2 text-amber-700 font-display font-extrabold text-lg"><Sparkles className="h-5 w-5" /> WE'RE NOT THERE YET!</div>
                  <p className="mt-1 text-slate-700 text-sm">BOCO BINS doesn't currently service this area, but we're growing! Leave your info and we'll notify you when we expand.</p>
                  {leadSent ? (
                    <div className="mt-3 rounded-lg bg-white border border-amber-200 p-3 font-semibold text-boco-navy" data-testid="booking-lead-success">Thanks! We'll be in touch when we reach {zipState.zip}.</div>
                  ) : (
                    <form onSubmit={submitLead} className="mt-3 grid gap-3 sm:grid-cols-3">
                      <Input placeholder="Name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} data-testid="booking-lead-name" />
                      <Input placeholder="Email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} data-testid="booking-lead-email" />
                      <Input placeholder="Phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} data-testid="booking-lead-phone" />
                      <button type="submit" className="sm:col-span-3 rounded-full bg-boco-green px-6 py-2.5 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors" data-testid="booking-notify-btn">Notify Me When You Expand</button>
                    </form>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* STEP 2: PLAN + DETAILS (locked until zip approved) */}
        <div className={`mt-6 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm transition-opacity ${zipState?.serviced ? "" : "opacity-50 pointer-events-none select-none"}`}>
          <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-boco-green">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-boco-green text-boco-navy text-xs">2</span> CHOOSE YOUR PLAN
            {!zipState?.serviced && <Lock className="h-3.5 w-3.5 text-slate-400" />}
          </div>

          <div className="mt-4 grid gap-3 grid-cols-2 lg:grid-cols-4">
            {PLANS.map((p) => (
              <button key={p.key} onClick={() => { setPlan(p.key); if (p.key !== "monthly_service") setExtraCans(0); }} className={`rounded-2xl border-2 p-4 text-left transition-colors ${plan === p.key ? "border-boco-green bg-boco-greenlight" : "border-slate-200 hover:border-slate-300"}`} data-testid={`booking-plan-${p.key}`}>
                <div className="font-display font-extrabold text-boco-navy uppercase text-sm">{p.name}</div>
                <div className="mt-1 font-display text-2xl font-black text-boco-navy">${p.price}</div>
                <div className="text-xs text-slate-500">{p.cadence}</div>
              </button>
            ))}
          </div>

          {isMonthly && (
            <div className="mt-4 rounded-2xl border-2 border-slate-200 p-4" data-testid="extra-cans-control">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-display font-bold text-boco-navy text-sm">Additional cans</div>
                  <div className="text-xs text-slate-500">Monthly plan covers 2 cans. Add more for ${EXTRA_CAN_PRICE}/mo each.</div>
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setExtraCans(Math.max(0, extraCans - 1))} disabled={extraCans === 0} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-boco-navy hover:bg-slate-100 disabled:opacity-40 transition-colors" data-testid="extra-cans-minus"><Minus className="h-4 w-4" /></button>
                  <span className="w-8 text-center font-display text-lg font-black text-boco-navy" data-testid="extra-cans-count">{extraCans}</span>
                  <button type="button" onClick={() => setExtraCans(Math.min(20, extraCans + 1))} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-boco-navy hover:bg-slate-100 transition-colors" data-testid="extra-cans-plus"><Plus className="h-4 w-4" /></button>
                </div>
              </div>
              {extra > 0 && <div className="mt-3 text-xs font-semibold text-boco-greendark">Total cans covered: {2 + extra} · +${extra * EXTRA_CAN_PRICE}/mo</div>}
            </div>
          )}

          <form onSubmit={checkout} className="mt-6 grid gap-4 sm:grid-cols-2">
            <div><Label className="text-boco-navy font-semibold">Full Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" /></div>
            <div><Label className="text-boco-navy font-semibold">Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" /></div>
            <div><Label className="text-boco-navy font-semibold">Email</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" /></div>
            <div><Label className="text-boco-navy font-semibold">Preferred Date (optional)</Label><Input type="date" className="mt-1.5" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} data-testid="booking-date" /></div>
            <div className="sm:col-span-2"><Label className="text-boco-navy font-semibold">Street Address *</Label><Input className="mt-1.5" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="123 Main St" required data-testid="booking-street" /></div>
            <div><Label className="text-boco-navy font-semibold">City / Town *</Label><Input className="mt-1.5" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Buchanan" required data-testid="booking-city" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-boco-navy font-semibold">State *</Label><Input className="mt-1.5" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="VA" required data-testid="booking-state" /></div>
              <div><Label className="text-boco-navy font-semibold">ZIP</Label><Input className="mt-1.5 bg-slate-50" value={zipState?.zip || ""} readOnly data-testid="booking-zip-display" /></div>
            </div>
            <div className="sm:col-span-2"><Label className="text-boco-navy font-semibold">Notes (optional)</Label><Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Gate code, number of cans, special instructions..." data-testid="booking-notes" /></div>

            <div className="sm:col-span-2 flex items-center justify-between rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <div>
                <div className="text-sm text-slate-500">You're booking</div>
                <div className="font-display font-extrabold text-boco-navy" data-testid="booking-summary">
                  {selectedPlan?.name}{extra > 0 ? ` + ${extra} extra can${extra > 1 ? "s" : ""}` : ""} — ${total} {selectedPlan?.cadence}
                </div>
              </div>
              <button type="submit" disabled={submitting || !zipState?.serviced} className="rounded-full bg-boco-green px-7 py-3.5 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors disabled:opacity-70 flex items-center gap-2" data-testid="booking-checkout-btn">
                {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting...</> : "Proceed to Payment"}
              </button>
            </div>
          </form>
          <p className="mt-3 text-center text-xs text-slate-400 flex items-center justify-center gap-1 flex-wrap"><Lock className="h-3 w-3" /> Secure checkout powered by Stripe · By proceeding you agree to our <Link to="/terms" className="underline hover:text-boco-navy" data-testid="booking-terms-link">Terms of Service</Link> · Questions? Call {BRAND.phone}</p>
        </div>
      </div>
    </div>
  );
}
